import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem } from '@atlaskit/dropdown-menu';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import Popup from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { N100A, N500, R300 } from '@atlaskit/theme/colors';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useColorStore } from '@atlassian/eoc-color-store';
import { ResponderAvatar, useResponderNames } from '@atlassian/eoc-responder-names';
import {
	filterNoOne,
	mergeRefs,
	commonMessages as scheduleCommonMessages,
} from '@atlassian/eoc-schedule-common';
import { Skeleton } from '@atlassian/eoc-skeleton';

import { messages as commonMessages } from '../../../../../../../common/messages';
import { getResponder } from '../../../../../../../utils/responder';
import { focus } from '../../../../../../../utils/window';

import { messages } from './messages';
import { ReplaceWith } from './replace-with';
import { type ParticipantProps } from './types';

const indexStyles = xcss({
	font: token('font.body.small'),
	marginTop: '0',
	marginRight: 'space.075',
	marginBottom: '0',
	marginLeft: '0',
	maxWidth: '16px',
	minWidth: '16px',
	textAlign: 'right',
});

const colorStyles = xcss({
	height: '20px',
	minWidth: '20px',
	maxWidth: '20px',
	borderRadius: 'border.radius.100',
	marginTop: '0',
	marginRight: 'space.100',
	marginBottom: '0',
	marginLeft: '0',
});

const missingResponderStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	height: '20px',
	minWidth: '20px',
	maxWidth: '20px',
	marginTop: '0',
	marginRight: 'space.100',
	marginBottom: '0',
	marginLeft: '0',
});

const avatarStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	marginTop: '0',
	marginRight: 'space.100',
	marginBottom: '0',
	marginLeft: '0',
});

const nameStyles = xcss({
	fontWeight: 'font.weight.regular',
	marginTop: '0',
	marginRight: 'auto',
	marginBottom: '0',
	marginLeft: '0',
	minWidth: '0',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const disabledTextStyles = xcss({
	color: 'color.text.disabled',
});

const normalTextStyles = xcss({
	color: 'color.text',
});

const optionsStyles = xcss({
	marginTop: '0',
	marginRight: 'space.050',
	marginBottom: '0',
	marginLeft: 'space.100',
	minWidth: '32px',
	maxWidth: '32px',
});

// NOTE: Focus/Blur not properly implemented
const Participant = ({
	identifier,
	order,
	responder,
	onRemove,
	onReplaceWith,
	onReplaceWithNoOne,
	isDisabled,
	onFocus,
	onBlur,
}: ParticipantProps) => {
	const intlApi = useIntl();
	const { getColor } = useColorStore();
	const [isPopupOpen, setIsPopupOpen] = useState(false);

	const onPopupOpen = useCallback(() => {
		setIsPopupOpen(true);
		onFocus?.();
	}, [onFocus]);

	const onPopupClose = useCallback(() => {
		setIsPopupOpen(false);
		onBlur?.();
	}, [onBlur]);

	const [user] = useResponderNames(filterNoOne([responder]));

	const color = getColor(responder.id);

	// TODO: handle error. See: TARDIS-7446
	const { name, type, deleted } = getResponder(responder, user, intlApi);

	const isNoOne = type === 'noone';
	const isDeleted = deleted;

	return (
		<>
			<Box
				xcss={indexStyles}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={{ color: token('color.chart.neutral', N500) }}
			>
				{order}
			</Box>

			{!(isNoOne || isDeleted) && (
				<Box
					xcss={colorStyles}
					style={{
						backgroundColor: isDisabled
							? token('color.skeleton.subtle', N100A)
							: color.backgroundColor,
					}}
				/>
			)}

			{isDeleted && (
				<Box
					xcss={missingResponderStyles}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={{ color: token('color.icon.danger', R300) }}
				>
					<ErrorIcon
						color="currentColor"
						LEGACY_size="medium"
						spacing="spacious"
						label={intlApi.formatMessage(scheduleCommonMessages.missingResponder)}
					/>
				</Box>
			)}

			{!isNoOne && (
				<Box xcss={avatarStyles}>
					<ResponderAvatar responder={user} size="xsmall" appearance="circle" />
				</Box>
			)}

			<Box xcss={[nameStyles, isDisabled ? disabledTextStyles : normalTextStyles]} title={name}>
				{!isNoOne && user.isLoading ? (
					<Skeleton height={2} width={15} />
				) : type === 'user' && isDeleted ? (
					intlApi.formatMessage(messages.deactivated, { name })
				) : (
					name
				)}
			</Box>

			<Box xcss={optionsStyles}>
				<DropdownMenu
					onOpenChange={({ isOpen }) => {
						if (isOpen) {
							onFocus?.();
						} else {
							onBlur?.();
						}
					}}
					trigger={({ triggerRef: dropdownTriggerRef, ...props }) => (
						<Popup
							autoFocus
							content={() => (
								<ReplaceWith
									ignored={responder}
									onClose={() => {
										onPopupClose();
										focus(identifier);
									}}
									onConfirm={({ value, type }) => {
										onPopupClose();
										onReplaceWith({ id: value, type });
									}}
								/>
							)}
							isOpen={isPopupOpen}
							onClose={() => {
								onPopupClose();
								focus(identifier);
							}}
							placement="bottom-start"
							zIndex={layers.modal() + 1}
							trigger={({ ref: popupTriggerRef }) => (
								<Tooltip content={intlApi.formatMessage(messages.options)} position="bottom">
									<IconButton
										{...props}
										label={intlApi.formatMessage(messages.actionsForParticipant, {
											participantName: name,
										})}
										id={identifier}
										isDisabled={isDisabled}
										appearance="subtle"
										icon={MoreIcon}
										ref={mergeRefs(dropdownTriggerRef, popupTriggerRef)}
									/>
								</Tooltip>
							)}
						/>
					)}
				>
					<DropdownItem onClick={onRemove}>
						{intlApi.formatMessage(commonMessages.remove)}
					</DropdownItem>
					<DropdownItem onClick={onPopupOpen}>
						{intlApi.formatMessage(commonMessages.replaceWith)}
					</DropdownItem>
					<DropdownItem onClick={onReplaceWithNoOne}>
						{intlApi.formatMessage(messages.replaceWithNoOne)}
					</DropdownItem>
				</DropdownMenu>
			</Box>
		</>
	);
};

export { Participant };
